import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Label } from "@/ui/label";
import { Textarea } from "@/ui/textarea";
import {
  useNominateBeauty,
  useNominateBlindBeauty,
  useWithdrawBeauty,
  useWithdrawBlindBeauty,
  type BeautyNomination,
  type BeautyNominationInput,
  type BlindBeautyNomination,
} from "./api";

/**
 * Năm điểm đẹp — bấm chọn NHIỀU (hợp đồng khachdep.v1 §8).
 *
 * Mã đi lên máy chủ là mã của hợp đồng; nhãn tiếng Việt chỉ để người đọc.
 * Đổi nhãn thì không sao, đổi MÃ là đổi bản tin — Marketing ra hợp đồng, bên
 * gửi không được tự sửa.
 */
const TRAITS = [
  { code: "guong_mat", label: "Gương mặt" },
  { code: "voc_dang", label: "Vóc dáng" },
  { code: "than_thai", label: "Thần thái" },
  { code: "an_anh", label: "Rất ăn ảnh" },
  { code: "phong_cach", label: "Phong cách riêng" },
] as const;

/** Ai đẹp — chọn MỘT. */
const SUBJECTS = [
  { code: "khach_chinh", label: "Khách chính" },
  { code: "co_dau", label: "Cô dâu" },
  { code: "chu_re", label: "Chú rể" },
  { code: "ca_hai", label: "Cả hai" },
  { code: "nguoi_khac", label: "Người khác" },
] as const;

/**
 * Mã đúng theo hợp đồng, suy thẳng từ bảng trên — khai lại bằng tay là để hai
 * danh sách trôi lệch nhau, và một mã lạ chỉ vỡ ở máy chủ.
 */
type TraitCode = (typeof TRAITS)[number]["code"];
type SubjectCode = (typeof SUBJECTS)[number]["code"];

const isTrait = (v: string): v is TraitCode => TRAITS.some((t) => t.code === v);
const isSubject = (v: string | undefined): v is SubjectCode =>
  !!v && SUBJECTS.some((s) => s.code === v);

type Props = {
  orderId: string;
  /** Đề cử đang có, hoặc null khi đơn chưa ai đề cử. */
  current: BeautyNomination | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function BeautyNominationDialog({ orderId, current, open, onOpenChange }: Props) {
  const active = current?.status === "active" ? current : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* Nội dung dựng LẠI mỗi lần mở, và mỗi khi đề cử sang bản sửa mới.
            `key` làm việc mà một useEffect "nạp lại lựa chọn cũ" từng làm —
            nhưng đặt state trong effect thì React phải vẽ hai lần và ESLint
            chặn đúng chỗ đó. Dựng lại bằng key là cùng kết quả, một lượt vẽ. */}
        {open ? (
          <OrderNominationForm
            key={`${orderId}:${active?.revision ?? 0}`}
            orderId={orderId}
            active={active}
            onOpenChange={onOpenChange}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function OrderNominationForm({
  orderId,
  active,
  onOpenChange,
}: {
  orderId: string;
  active: BeautyNomination | null;
  onOpenChange: (v: boolean) => void;
}) {
  const nominate = useNominateBeauty(orderId);
  const withdraw = useWithdrawBeauty(orderId);
  return (
    <NominationForm
      nominate={nominate}
      withdraw={withdraw}
      active={active}
      byLabel={active?.nominatedByName ?? ""}
      onOpenChange={onOpenChange}
    />
  );
}

/** Hộp đề cử trên màn Chấm bài: gửi theo mã bài, không biết mã đơn. */
export function BlindBeautyDialog({
  queueId,
  current,
  open,
  onOpenChange,
}: {
  queueId: number;
  current: BlindBeautyNomination | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const active = current?.mine ? current : null;
  const nominate = useNominateBlindBeauty(queueId);
  const withdraw = useWithdrawBlindBeauty(queueId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {open ? (
          <NominationForm
            key={`${queueId}:${active?.revision ?? 0}`}
            nominate={nominate}
            withdraw={withdraw}
            active={active}
            byLabel="Bạn"
            onOpenChange={onOpenChange}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Một hàng nút tròn bấm chọn. Dùng chung cho cả hai câu hỏi — hai bản sao của
 * cùng một khối là hai chỗ để kiểu dáng và trạng thái "đang chọn" trôi lệch.
 */
function ChoiceChips<T extends string>({
  label,
  options,
  isOn,
  onPick,
}: {
  label: string;
  options: readonly { readonly code: T; readonly label: string }[];
  isOn: (code: T) => boolean;
  onPick: (code: T) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = isOn(o.code);
          return (
            <button
              key={o.code}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(o.code)}
              className={
                "rounded-full border px-3 py-1.5 text-sm transition " +
                (on
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background hover:bg-accent")
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NoteField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label htmlFor="beauty-note">Ghi chú (không bắt buộc)</Label>
      <Textarea
        id="beauty-note"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ví dụ: da đẹp, lên hình rất ăn đèn"
      />
    </div>
  );
}

function NominationForm({
  nominate,
  withdraw,
  active,
  byLabel,
  onOpenChange,
}: {
  nominate: { mutateAsync: (input: BeautyNominationInput) => Promise<unknown>; isPending: boolean };
  withdraw: { mutateAsync: (reason: string) => Promise<unknown>; isPending: boolean };
  active: Pick<BeautyNomination, "traits" | "subject" | "note" | "delivered"> | null;
  /** Ai đã đề cử, hiện dưới hộp khi đang sửa. */
  byLabel: string;
  onOpenChange: (v: boolean) => void;
}) {
  // Khởi tạo từ đề cử đang có: mở lại để sửa một ô mà phải gõ lại từ đầu là
  // cách nhanh nhất làm người ta bỏ luôn.
  //
  // Lọc chứ không ép kiểu mù: máy chủ trả chuỗi tự do, và một mã lạ (hợp đồng
  // nới thêm về sau, hoặc dòng cũ) phải rơi ra ở đây chứ không đi tiếp vào
  // một ô bấm không có thật.
  const [traits, setTraits] = useState<TraitCode[]>(() => (active?.traits ?? []).filter(isTrait));
  const [subject, setSubject] = useState<SubjectCode>(() =>
    isSubject(active?.subject) ? active.subject : "khach_chinh",
  );
  const [note, setNote] = useState(active?.note ?? "");

  const toggleTrait = (code: TraitCode) => {
    setTraits((cur) => (cur.includes(code) ? cur.filter((t) => t !== code) : [...cur, code]));
  };

  const submit = async () => {
    if (traits.length === 0) {
      toast.error("Chọn ít nhất một điểm đẹp");
      return;
    }
    try {
      await nominate.mutateAsync({ traits, subject, note: note.trim() });
    } catch (e) {
      // Giữ hộp MỞ khi máy chủ từ chối: đóng lặng lẽ là để người bấm tưởng
      // đề cử đã vào sổ.
      toast.error(e instanceof Error ? e.message : "Không gửi được đề cử");
      return;
    }
    toast.success(active ? "Đã cập nhật đề cử" : "Đã gửi đề cử cho marketing");
    onOpenChange(false);
  };

  const remove = async () => {
    try {
      await withdraw.mutateAsync("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Không gỡ được đề cử");
      return;
    }
    toast.success("Đã gỡ đề cử");
    onOpenChange(false);
  };

  const busy = nominate.isPending || withdraw.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Đơn này đẹp, marketing nên xem</DialogTitle>
        <DialogDescription>
          Chỉ cần hai lựa chọn. Ảnh, gói, ngày chụp, ê-kíp và thông tin khách máy tự kèm theo.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <ChoiceChips
          label="Đẹp ở điểm gì?"
          options={TRAITS}
          isOn={(code) => traits.includes(code)}
          onPick={toggleTrait}
        />
        <ChoiceChips
          label="Ai đẹp?"
          options={SUBJECTS}
          isOn={(code) => subject === code}
          onPick={setSubject}
        />

        <NoteField value={note} onChange={setNote} />

        {active ? (
          <p className="text-xs text-muted-foreground">
            {byLabel} đã đề cử đơn này
            {active.delivered ? "" : " — đang chờ gửi sang marketing"}.
          </p>
        ) : null}
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {active ? (
          <Button type="button" variant="ghost" onClick={remove} disabled={busy}>
            Gỡ đề cử
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Huỷ
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {active ? "Lưu thay đổi" : "Gửi cho marketing"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
